import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { DURATION } from '../../styles/animations';
import countriesConfig from '../../../shared-config/countries.config.json';
import { FacetOptionRow } from './FacetOptionRow';
import { resolveCandidateRows, type EntityFilterCandidates } from './filterFacets';

// Type for country data from config
interface CountryConfigData {
  name: string;
  currency: string;
  language: string;
  numberFormat: string;
  dateFormat: string;
}

// Flag component using country-flag-icons CDN
function CountryFlag({ code, size = 20 }: { code: string; size?: number }) {
  const flagUrl = `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`;
  return (
    <img
      src={flagUrl}
      alt={code}
      width={size * 1.5}
      height={size}
      style={{ objectFit: 'cover', borderRadius: 2 }}
      loading="lazy"
    />
  );
}

export interface CountryOption {
  code: string;
  name: string;
  currency: string;
  language: string;
  numberFormat: string;
  dateFormat: string;
}

interface CountryPickerProps extends EntityFilterCandidates {
  value: string | null;
  onChange: (code: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  /** Show a "clear" option at the top of the list to reset to null. Label uses placeholder text. */
  allowClear?: boolean;
}

// Build sorted country options from config
const countryOptions: CountryOption[] = Object.entries(
  countriesConfig.countries as Record<string, CountryConfigData>
)
  .map(([code, data]) => ({
    code,
    name: data.name,
    currency: data.currency,
    language: data.language,
    numberFormat: data.numberFormat,
    dateFormat: data.dateFormat,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Map for quick lookup
const countryMap = new Map(countryOptions.map((c) => [c.code, c]));

export function CountryPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  allowClear = false,
  candidates,
  onShowAllCandidates,
  showAllCandidatesLabel,
}: CountryPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const resolvedPlaceholder = placeholder ?? t('pickers.selectCountry');

  const selectedOption = useMemo(
    () => (value ? countryMap.get(value) || null : null),
    [value]
  );
  const facetRows = useMemo(
    () =>
      candidates === undefined
        ? null
        : resolveCandidateRows({
            candidates,
            search,
            value,
            selectedLabel: value ? getCountryName(value) : undefined,
          }),
    [candidates, search, value],
  );

  // Whether to BUILD the option list at all — see the guard on the popover body
  // below for why that is worth deciding. It is deliberately not `open`: Radix
  // keeps the closed panel mounted for its exit animation (`POPOVER_CONTENT`,
  // `duration-150`), so clearing the content the moment `open` flips would fade
  // an EMPTY card out on every close, in every consumer of this picker.
  const [renderList, setRenderList] = useState(false);
  useEffect(() => {
    if (open) {
      setRenderList(true);
      return;
    }
    const timer = setTimeout(() => setRenderList(false), DURATION.slow);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label required={required}>
            {label}
          </Label>
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedOption && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {selectedOption ? (
              <span className="flex items-center gap-2">
                <CountryFlag code={selectedOption.code} size={16} />
                <span className="truncate">{selectedOption.name}</span>
              </span>
            ) : (
              resolvedPlaceholder
            )}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {/* The list is built ONLY while this picker is open (plus its exit
            animation — see `renderList`). Radix renders no content when closed,
            but JSX evaluates a component's children eagerly, so without this
            guard every render of a CLOSED picker still constructs ~250
            `CommandItem` element trees that nothing will ever mount. One picker
            per row of a line-item grid turns that into thousands of throwaway
            elements per keystroke (WI 0110f621). */}
        <PopoverContent className="w-[300px] max-w-[calc(100vw-2rem)] p-0" align="start">
          {renderList && (
          <Command shouldFilter={facetRows === null}>
            <CommandInput
              placeholder={t('pickers.searchCountry')}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>{t('pickers.noCountryFound')}</CommandEmpty>
              <CommandGroup>
                {allowClear && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onChange(null);
                      setSearch('');
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4',
                        !value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="text-muted-foreground">{resolvedPlaceholder}</span>
                  </CommandItem>
                )}
                {(facetRows ?? countryOptions).map((option) => {
                  const code = 'id' in option ? option.id : option.code;
                  const name = 'label' in option ? option.label : option.name;
                  const count = 'count' in option ? option.count : undefined;
                  return (
                  <CommandItem
                    key={code}
                    value={facetRows === null ? `${name} ${code}` : code}
                    onSelect={() => {
                      onChange(code === value ? null : code);
                      setSearch('');
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4',
                        value === code ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <CountryFlag code={code} size={16} />
                    {facetRows === null ? (
                      <>
                        <span className="ms-2 flex-1">{name}</span>
                        <span className="text-muted-foreground text-xs">{code}</span>
                      </>
                    ) : (
                      <span className="ms-2 flex min-w-0 flex-1">
                        <FacetOptionRow label={name} count={count} />
                      </span>
                    )}
                  </CommandItem>
                  );
                })}
              </CommandGroup>
              {facetRows !== null && onShowAllCandidates && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      value="__show_all_candidates__"
                      onSelect={onShowAllCandidates}
                      className="text-muted-foreground touch:h-11"
                    >
                      <Search className="me-2 h-4 w-4 shrink-0" />
                      {showAllCandidatesLabel ?? t('app.showAllOptions')}
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
          )}
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

// Export helper functions and data for external use
export function getCountryByCode(code: string): CountryOption | undefined {
  return countryMap.get(code);
}

export function getCountryName(code: string): string {
  return countryMap.get(code)?.name || code;
}

export function getAllCountries(): CountryOption[] {
  return countryOptions;
}

export { CountryFlag };
